import { useEffect } from 'react';
import {
  Controller,
  FieldPath,
  UseFormReturn,
  useFieldArray,
} from 'react-hook-form';

import { Checkbox } from 'components/Checkbox';
import FormInputWrapper from 'components/input/FormInputWrapper';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import { useFieldAttributes } from 'hooks/useLoadContext';
import {
  LoadDateTimeInput,
  LoadSelectInput,
} from 'pages/LoadView/LoadInformation/Components';
import { NormalizedLoad } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { datetimeFieldOptions } from 'utils/formValidators';

import { LoadBuildingTextInput } from './Customer';

interface StopFormProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  stopType: 'pickup' | 'consignee';
  load?: NormalizedLoad;
  isCreateMode?: boolean;
}

const rescheduleReasonOptions = [
  {
    value: 'customer_issue__shipping_facility',
    label: 'Customer Issue: Shipping Facility',
  },
  {
    value: 'customer_issue__receiving_facility',
    label: 'Customer Issue: Receiving Facility',
  },
  {
    value: 'customer_issue__customer_requested_change',
    label: 'Customer Issue: Customer Requested Change',
  },
  {
    value: 'no_fault__first_appointment',
    label: 'No Fault: First Appointment',
  },
  {
    value: 'tf_issue__cm_booking_capacity',
    label: 'TF Issue: CM Booking/Capacity',
  },
  {
    value: 'carrier_failure__poor_planning',
    label: 'Carrier Failure: Poor Planning',
  },
  {
    value: 'carrier_issue__driver_issue',
    label: 'Carrier Issue: Driver Issue',
  },
  { value: 'carrier_issue__mechanical', label: 'Carrier Issue: Mechanical' },
  {
    value: 'carrier_issue__driving_delays',
    label: 'Carrier Issue: Driver Delays',
  },
  {
    value: 'carrier_issue__rejected_trailer',
    label: 'Carrier Issue: Rejected trailer',
  },
  {
    value: 'no_fault__weather_aog_holiday_road_closure',
    label: 'No Fault: Weather/AOG/Holiday/Road Closure',
  },
  { value: 'no_fault__smoothing', label: 'No Fault: Smoothing' },
  {
    value: 'customer_issue__edi_incorrect_info',
    label: 'Customer Issue: EDI Incorrect Info',
  },
  {
    value: 'customer_issue__clerical_error_inaccurate_info',
    label: 'Customer Issue: Clerical Error/Inaccurate Info',
  },
  { value: 'tf_issue__loss_prevention', label: 'TF Issue: Loss Prevention' },
  {
    value: 'tf_issue__clerical_error_inaccurate_info',
    label: 'TF Issue: Clerical Error/Inaccurate Info',
  },
  { value: 'tf_issue__pricing', label: 'TF Issue: Pricing' },
  { value: 'tf_issue__tracking_tracing', label: 'TF Issue: Tracking/Tracing' },
];

export function StopForm({
  formMethods,
  stopType,
  load,
  isCreateMode,
}: StopFormProps) {
  const { watch, setValue, control } = formMethods;
  const fieldAttrs = useFieldAttributes();
  const prefix = stopType;
  const refNumberAttr = getFieldAttribute(fieldAttrs, `${prefix}.refNumber`);
  const { fields } = useFieldArray({
    control,
    name: `${prefix}.additionalReferences`,
  });

  // Watch appointment type to conditionally show fields
  const watchedApptType = watch(`${prefix}.apptType`);

  const currentLoad = watch(); // Get current form values

  const timezone = watch(`${prefix}.timezone`);
  const addressLine1 = watch(`${prefix}.addressLine1`);
  const addressLine2 = watch(`${prefix}.addressLine2`);

  // Detect if appointment times have changed
  const watchedStartTime = watch(`${prefix}.apptStartTime`);
  const watchedEndTime = watch(`${prefix}.apptEndTime`);

  const initialStartTime = load?.[prefix]?.apptStartTime;
  const initialEndTime = load?.[prefix]?.apptEndTime;

  const isStartTimeChanged =
    initialStartTime &&
    watchedStartTime &&
    new Date(initialStartTime).getTime() !==
      new Date(watchedStartTime).getTime();
  const isEndTimeChanged =
    initialEndTime &&
    watchedEndTime &&
    new Date(initialEndTime).getTime() !== new Date(watchedEndTime).getTime();

  const showRescheduleReason =
    !isCreateMode && (isStartTimeChanged || isEndTimeChanged);

  const capitalizedPrefix = prefix?.charAt(0).toUpperCase() + prefix?.slice(1);

  useEffect(() => {
    if (!showRescheduleReason) {
      setValue(`${prefix}.rescheduleReason`, '');
    }
  }, [showRescheduleReason, setValue, prefix]);

  useEffect(() => {
    if (watchedApptType === 'appointment') {
      setValue(`${prefix}.apptEndTime`, null);
    }
  }, [watchedApptType, setValue, prefix]);

  const getRefIndexByQualifier = (qualifier: string): number => {
    return fields.findIndex((field) => field.qualifier === qualifier);
  };

  const confirmationRefIndex = getRefIndexByQualifier('CONFIRMATION');

  return (
    <>
      {addressLine1 && (
        <RHFTextInput
          name={`${prefix}.addressLine1`}
          label='Address Line 1'
          readOnly={true}
        />
      )}
      {addressLine2 && (
        <RHFTextInput
          name={`${prefix}.addressLine2`}
          label='Address Line 2'
          readOnly={true}
        />
      )}
      <RHFTextInput name={`${prefix}.city`} label='City' readOnly={true} />
      <Flex direction='row' gap='md'>
        <Flex grow='1'>
          <LoadBuildingTextInput
            name={`${prefix}.state`}
            label='State'
            readOnly
          />
        </Flex>
        <Flex grow='1'>
          <LoadBuildingTextInput
            name={`${prefix}.zipCode`}
            label='ZIP Code'
            readOnly
            placeholder='12345'
          />
        </Flex>
      </Flex>
      {!refNumberAttr?.isNotSupported && (
        <LoadRefPillInput
          name={`${prefix}.refNumber`}
          control={control}
          label='Reference Number'
          disabled={true}
          originalSuggestionData={load || formMethods.getValues()}
        />
      )}

      {confirmationRefIndex !== -1 && (
        <LoadBuildingTextInput
          name={
            `${prefix}.additionalReferences.${confirmationRefIndex}.number` as FieldPath<NormalizedLoad>
          }
          label={`${capitalizedPrefix} #`}
          placeholder={`${capitalizedPrefix} #`}
          readOnly
        />
      )}

      <Controller
        name={`${prefix}.apptConfirmed`}
        control={control}
        render={({ field }) => (
          <Checkbox
            label='Confirm Appointment'
            labelClassName='leading-none text-neutral-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
            checked={field.value ?? false}
            onCheckedChange={(checked) => field.onChange(checked)}
          />
        )}
      />

      <FormInputWrapper
        name={`${prefix}.businessHours`}
        label='Business Hours'
        readOnly
      >
        <div className='w-full rounded-[4px] border border-neutral-input-disabled-border bg-neutral-input-disabled-bg px-3 py-2 text-[13px] text-neutral-input-disabled-text whitespace-pre-line cursor-not-allowed'>
          {watch(`${prefix}.businessHours`)}
        </div>
      </FormInputWrapper>

      <LoadSelectInput
        name={`${prefix}.apptType`}
        label='Appointment Type'
        options={[
          { value: 'appointment', label: 'Appointment' },
          { value: 'range', label: 'Range' },
        ]}
        placeholder='Select appointment type'
      />

      {watchedApptType && (
        <>
          <LoadDateTimeInput
            name={`${prefix}.apptStartTime`}
            label='Appointment Start Time'
            options={datetimeFieldOptions}
            defaultTimezone='UTC'
            load={load}
            disablePastDates={false}
          />

          {watchedApptType === 'range' && (
            <LoadDateTimeInput
              name={`${prefix}.apptEndTime`}
              label='Appointment End Time'
              options={datetimeFieldOptions}
              defaultTimezone='UTC'
              load={load}
              disablePastDates={false}
            />
          )}

          {showRescheduleReason && (
            <LoadSelectInput
              name={`${prefix}.rescheduleReason`}
              label='Reschedule Reason'
              options={rescheduleReasonOptions}
              placeholder='Select reason'
              required={true}
            />
          )}

          <RHFTextInput name={`${prefix}.apptNote`} label='Appointment Note' />
        </>
      )}

      {prefix === 'pickup' && (
        <>
          <LoadDateTimeInput
            name='carrier.expectedPickupTime'
            label='Manual ETA'
            options={datetimeFieldOptions}
            load={currentLoad}
            disablePastDates={false}
            timezoneOverride={timezone}
          />
          <LoadDateTimeInput
            name='carrier.pickupStart'
            label='Checked In'
            options={datetimeFieldOptions}
            load={currentLoad}
            disablePastDates={false}
            timezoneOverride={timezone}
          />
          <LoadDateTimeInput
            name='carrier.pickupEnd'
            label='Checked Out'
            options={datetimeFieldOptions}
            load={currentLoad}
            disablePastDates={false}
            timezoneOverride={timezone}
          />
        </>
      )}

      {prefix === 'consignee' && (
        <>
          <LoadDateTimeInput
            name='carrier.expectedDeliveryTime'
            label='Manual ETA'
            options={datetimeFieldOptions}
            load={currentLoad}
            disablePastDates={false}
            timezoneOverride={timezone}
          />
          <LoadDateTimeInput
            name='carrier.deliveryStart'
            label='Checked In'
            options={datetimeFieldOptions}
            load={currentLoad}
            disablePastDates={false}
            timezoneOverride={timezone}
          />
          <LoadDateTimeInput
            name='carrier.deliveryEnd'
            label='Checked Out'
            options={datetimeFieldOptions}
            load={currentLoad}
            disablePastDates={false}
            timezoneOverride={timezone}
          />
        </>
      )}
    </>
  );
}

export default StopForm;
